import {
  ProposalBoxType,
  ProposalMove,
  ProposalOutcome,
  ProposalStage,
} from "work/proposals/__constants/proposal-lifecycle";
import type { ListViewPath } from "_shared/_views/list-pages/current-table-view";
import {
  readListChips,
  writeListChips,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";

/**
 * The kinds of thing the Proposals page lists, one tab each (#6473).
 *
 * Tabs separate things that are genuinely different. Which proposals the Proposals tab shows --
 * drafts, active or archived, and what they need from the reader -- is a filter on one kind of
 * thing, so it is a chip (`ProposalStageChip`, `ProposalMove`), not a tab.
 */
export enum PageTab {
  /** Every proposal, filtered by the stage and move chips. Its path is the page's own, `/proposals`. */
  Proposals = "proposals",
  /** The reader's saved proposal templates (#6261). */
  Templates = "templates",
}

const pageTabs = Object.values(PageTab) as string[];

export function isPageTab(value: string | null | undefined): value is PageTab {
  return value !== null && value !== undefined && pageTabs.includes(value);
}

/** The path a tab is at. The Proposals tab is the page's own path, with no slug. */
export function pageTabPath(tab: PageTab): string {
  return tab === PageTab.Proposals ? "/proposals" : `/proposals/${tab}`;
}

/** The query parameter of the stage chips. `WorkRecordListLink.ProposalStageParameter` writes it too. */
export const STAGE_PARAMETER = "stage";

/** The query parameter of the move chips. */
export const MOVE_PARAMETER = "move";

/** The query parameter of the archive's outcome chip (#6576). */
export const OUTCOME_PARAMETER = "outcome";

/**
 * The stage chips, as the URL spells them. No chip is the list of every proposal that is not
 * archived, which is what the assistant counts as the current proposals.
 */
export enum ProposalStageChip {
  Drafts = "draft",
  Active = "active",
  Archived = "archived",
}

const stageChips = Object.values(ProposalStageChip) as string[];

/**
 * The stage value that names every open proposal outright: what no stage chip shows, said by a link.
 *
 * A plain `/proposals` reopens the view the reader last chose, so a link that means every open
 * proposal -- the assistant's, or an old `/proposals/current` one -- says so, and keeps its meaning
 * whatever the reader chose before. It is no chip: the list reads it as no stage.
 */
export const OPEN_STAGE = "open";

/**
 * The archive's outcome chips, as the URL spells them (#6576). No chip is the whole archive, which
 * also holds the proposals that were rejected or revised: those ended in no outcome a chip names.
 */
export enum ProposalOutcomeChip {
  Hired = "hired",
  Canceled = "canceled",
  AwardedElsewhere = "awarded-elsewhere",
}

/**
 * The move chips that can match a proposal in one of `stages`, in the order they are drawn; no stage
 * is the open list.
 *
 * A draft waits on nobody else -- its side has not sent it -- so Their move has nothing to list
 * there, and an archived proposal needs nothing from anyone. Several stages offer what any of them
 * offers.
 */
export function movesOfStages(stages: readonly string[]): ProposalMove[] {
  const all = [ProposalMove.YourMove, ProposalMove.YourReview, ProposalMove.TheirMove];
  if (stages.length === 0) return all;
  return all.filter((move) =>
    stages.some((stage) => {
      if (stage === ProposalStageChip.Archived) return false;
      if (stage === ProposalStageChip.Drafts) return move !== ProposalMove.TheirMove;
      return true;
    }),
  );
}

/**
 * The Proposals tab's chip groups (#6473, #6576, #6712): the stage, what the proposal needs from the
 * reader, and, in the archive, how it ended.
 *
 * The stage chips widen: Drafts and Active together are the open list, which is no chip, and Active
 * with Archived lists both. The move chips narrow the stage, and are drawn only where they can match.
 * The outcome chips are drawn only while the archive is the whole list, and the API takes one of them.
 */
export const proposalChipGroups: readonly ListChipGroup[] = [
  {
    parameter: STAGE_PARAMETER,
    label: "Stage",
    chips: [
      { value: ProposalStageChip.Drafts, label: "Drafts" },
      { value: ProposalStageChip.Active, label: "Active" },
      { value: ProposalStageChip.Archived, label: "Archived" },
    ],
    sameAsNone: [ProposalStageChip.Drafts, ProposalStageChip.Active],
    allLabel: "Open",
  },
  {
    parameter: MOVE_PARAMETER,
    label: "Needs",
    allLabel: "Anything",
    chips: [
      { value: ProposalMove.YourMove, label: "Your move" },
      { value: ProposalMove.YourReview, label: "Your review" },
      { value: ProposalMove.TheirMove, label: "Their move" },
    ],
    chipsGiven: (selection) => movesOfStages(selection[STAGE_PARAMETER] ?? []),
  },
  {
    parameter: OUTCOME_PARAMETER,
    label: "How it ended",
    chips: [
      { value: ProposalOutcomeChip.Hired, label: "Hired" },
      { value: ProposalOutcomeChip.Canceled, label: "Canceled" },
      { value: ProposalOutcomeChip.AwardedElsewhere, label: "Awarded elsewhere" },
    ],
    chipsGiven: (selection) => (isArchiveAlone(selection) ? Object.values(ProposalOutcomeChip) : []),
    isSingle: true,
  },
];

/** Whether the list is the archive and nothing else. */
export function isArchiveAlone(selection: ListChipSelection): boolean {
  const stages = selection[STAGE_PARAMETER] ?? [];
  return stages.length === 1 && stages[0] === ProposalStageChip.Archived;
}

/** Whether the list holds archived proposals, alone or beside another stage. */
export function listsArchive(selection: ListChipSelection): boolean {
  return (selection[STAGE_PARAMETER] ?? []).includes(ProposalStageChip.Archived);
}

/** The chips `searchParams` names, as the Proposals tab reads them. */
export function readProposalChips(searchParams: URLSearchParams): ListChipSelection {
  return readListChips(proposalChipGroups, searchParams);
}

/** The outcome each chip asks `GET /work/proposals` for. */
export const apiOutcomes: Record<ProposalOutcomeChip, ProposalOutcome> = {
  [ProposalOutcomeChip.Hired]: ProposalOutcome.Hired,
  [ProposalOutcomeChip.Canceled]: ProposalOutcome.Canceled,
  [ProposalOutcomeChip.AwardedElsewhere]: ProposalOutcome.AwardedElsewhere,
};

/** What a list of proposals adds to the criteria in its URL, before the list is asked for. */
export type ProposalListFilter = {
  stage?: ProposalStage[];
  isArchived?: boolean;
  move?: ProposalMove[];
  outcome?: ProposalOutcome;
};

const apiStages: Record<string, ProposalStage> = {
  [ProposalStageChip.Drafts]: ProposalStage.Draft,
  [ProposalStageChip.Active]: ProposalStage.Active,
  [ProposalStageChip.Archived]: ProposalStage.Archived,
};

/**
 * What the chips add to the criteria in the URL.
 *
 * With no stage chip the list asks for what is not archived -- the condition
 * `Work.WorkRecords.WorkRecordListLink` writes a link for -- and otherwise for the chosen stages,
 * which the API reads as one `IN` (#6709). The move chips go the same way.
 */
export function proposalListFilter(selection: ListChipSelection): ProposalListFilter {
  const stages = selection[STAGE_PARAMETER] ?? [];
  const filter: ProposalListFilter = stages.length > 0
    ? { stage: stages.map((stage) => apiStages[stage]) }
    : { isArchived: false };
  const moves = selection[MOVE_PARAMETER] ?? [];
  if (moves.length > 0) filter.move = moves as ProposalMove[];
  const outcome = selection[OUTCOME_PARAMETER]?.[0];
  if (outcome) filter.outcome = apiOutcomes[outcome as ProposalOutcomeChip];
  return filter;
}

/** The list tabs the page had before #6473, as their slugs; each now opens a stage chip. */
const legacyListStages: Record<string, ProposalStageChip | undefined> = {
  active: ProposalStageChip.Active,
  drafts: ProposalStageChip.Drafts,
  current: undefined,
  archived: ProposalStageChip.Archived,
};

/** The boxes the page's old chips and the dashboard's links named, as the stage and move they meant. */
const legacyBoxes: Partial<Record<ProposalBoxType, { stage?: ProposalStageChip; move?: ProposalMove }>> = {
  [ProposalBoxType.Draft]: { stage: ProposalStageChip.Drafts },
  [ProposalBoxType.DraftAuthored]: { stage: ProposalStageChip.Drafts, move: ProposalMove.YourMove },
  [ProposalBoxType.DraftReview]: { stage: ProposalStageChip.Drafts, move: ProposalMove.YourReview },
  [ProposalBoxType.AllActive]: { stage: ProposalStageChip.Active },
  [ProposalBoxType.Inbox]: { stage: ProposalStageChip.Active, move: ProposalMove.YourMove },
  [ProposalBoxType.ActiveReview]: { stage: ProposalStageChip.Active, move: ProposalMove.YourReview },
  [ProposalBoxType.Outbox]: { stage: ProposalStageChip.Active, move: ProposalMove.TheirMove },
  [ProposalBoxType.Archive]: { stage: ProposalStageChip.Archived },
};

/** The one stage chip a link written before #6712 named, if it named one. */
function linkedStage(searchParams: URLSearchParams): ProposalStageChip | undefined {
  const value = searchParams.get(STAGE_PARAMETER)?.toLowerCase() ?? null;
  return value !== null && stageChips.includes(value) ? (value as ProposalStageChip) : undefined;
}

/**
 * Where a link to one of the old list tabs, or to a box, now opens; undefined when `slug` is not
 * one of those tabs and `searchParams` names no box.
 *
 * Bookmarks, notifications and the dashboard linked to `/proposals/active?boxType=Inbox` and its
 * siblings, so each still lands on the proposals it listed. Every other parameter is kept.
 */
export function legacyListPath(slug: string | undefined, searchParams: URLSearchParams): string | undefined {
  const isLegacyTab = slug !== undefined && Object.hasOwn(legacyListStages, slug);
  if (slug !== undefined && !isLegacyTab) return undefined;
  const boxType = searchParams.get("boxType");
  if (!isLegacyTab && boxType === null) return undefined;

  const box = boxType === null ? undefined : legacyBoxes[boxType as ProposalBoxType];
  const next = new URLSearchParams(searchParams);
  next.delete("boxType");
  const tabStage = slug !== undefined && isLegacyTab ? legacyListStages[slug] : linkedStage(searchParams);
  const stage = box?.stage ?? tabStage ?? (slug === "current" ? OPEN_STAGE : undefined);
  next.delete(STAGE_PARAMETER);
  next.delete(MOVE_PARAMETER);
  const ordered = new URLSearchParams();
  if (stage) ordered.set(STAGE_PARAMETER, stage);
  if (box?.move) ordered.set(MOVE_PARAMETER, box.move);
  for (const [name, value] of next) ordered.append(name, value);
  const query = ordered.toString();
  return query ? `/proposals?${query}` : "/proposals";
}

/**
 * The path of the proposal list showing `stages`, for the pages that go back to it -- after a draft
 * is deleted, say -- and the view a click on a stage chip remembers.
 */
export function proposalListPath(...stages: ProposalStageChip[]): string {
  const searchParams = writeListChips(proposalChipGroups, { [STAGE_PARAMETER]: stages }, new URLSearchParams());
  const query = searchParams.toString();
  return query ? `/proposals?${query}` : "/proposals";
}

/**
 * The stored view of the list (#6714) with what the page no longer has taken out: a tab it does not
 * have, and a chip value no group has any more. Undefined when the tab is gone. The table's own
 * parameters -- filters, sort, page size -- are kept as stored; the page reads only the ones it knows.
 *
 * It replaces the `work.proposals.listView` session copy, which kept the tab and the stage chips in
 * one browser tab only.
 */
export function keepProposalsView(view: ListViewPath): ListViewPath | undefined {
  const tab = (Object.values(PageTab) as PageTab[]).find((each) => pageTabPath(each) === view.tabPath);
  if (tab === undefined) return undefined;
  if (tab !== PageTab.Proposals) return { tabPath: view.tabPath, search: "" };
  const stored = new URLSearchParams(view.search);
  const kept = writeListChips(proposalChipGroups, readListChips(proposalChipGroups, stored), stored);
  return { tabPath: view.tabPath, search: kept.toString() };
}
